import { addMonths, format, isSameMonth } from 'date-fns';
import { memo, useCallback, useMemo } from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { FamilyEvent } from '@/features/guardian/family-events';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import FamilyCalendarDayCell from './day-cell';
import { buildCalendarWeeks } from './utils';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/**
 * Where the Mon–Sun legend sits relative to the white month card.
 * Tablet keeps it `inside` the card (under the month header); the phone Calendar
 * screen lifts it `outside`, above the card.
 */
export type FamilyCalendarWeekdayPlacement = 'inside' | 'outside';

export type FamilyCalendarProps = {
  month: Date;
  selectedDate: Date;
  events: FamilyEvent[];
  onChangeMonth: (month: Date) => void;
  onSelectDay: (day: Date) => void;
  /** Injectable so a mocked reference date can keep its outlined "today" state. */
  today?: Date;
  /**
   * Flex weight inside the parent. The tablet Kids screen pairs the calendar with the
   * day panel at `2.05`; the phone Calendar screen lets it fill the body (`1`).
   */
  flex?: number;
  weekdayPlacement?: FamilyCalendarWeekdayPlacement;
};

/** Month grid shared by the tablet Kids screen and the phone Calendar screen. */
function FamilyCalendar({
  month,
  selectedDate,
  events,
  onChangeMonth,
  onSelectDay,
  today = new Date(),
  flex = 1,
  weekdayPlacement = 'inside',
}: FamilyCalendarProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const weeks = useMemo(() => buildCalendarWeeks(month), [month]);
  const calendarMinHeight = scale({ phone: 0, tablet: weeks.length * 72 + 76 });
  const eventsByDate = useMemo(() => {
    const result = new Map<string, FamilyEvent[]>();
    events.forEach(event => {
      const current = result.get(event.date);
      if (current) current.push(event);
      else result.set(event.date, [event]);
    });
    return result;
  }, [events]);

  const handlePreviousMonth = useCallback(
    () => onChangeMonth(addMonths(month, -1)),
    [month, onChangeMonth],
  );
  const handleNextMonth = useCallback(
    () => onChangeMonth(addMonths(month, 1)),
    [month, onChangeMonth],
  );

  const isWeekdayInside = weekdayPlacement === 'inside';
  const weekdayRow = (
    <Stack
      direction="row"
      align="center"
      style={isWeekdayInside ? styles.weekdayRowInside : styles.weekdayRowOutside}>
      {WEEKDAYS.map(weekday => (
        <Stack key={weekday} flex={1} align="center">
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.weekday}>
            {weekday}
          </Text>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack flex={flex} minHeight={calendarMinHeight}>
      {isWeekdayInside ? null : weekdayRow}

      <Stack flex={1} borderRadius={scale(16)} backgroundColor={colors['0']} overflow="hidden">
        <Stack
          direction="row"
          align="center"
          justify="space-between"
          gap={scale(20)}
          paddingTop={scale(12)}
          paddingBottom={scale(8)}
          paddingX={scale(12)}>
          <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.monthLabel}>
            {format(month, 'MMMM, yyyy')}
          </Text>

          <Stack direction="row" gap={scale(20)}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={STRINGS.guardian.kids.previousMonth}
              hitSlop={8}
              onPress={handlePreviousMonth}
              style={({ pressed }) => [styles.navButton, pressed ? styles.navPressed : null]}>
              <SvgIcon
                name="IcChevronRight"
                size={scale(24)}
                color={guardianColors['6']}
                style={styles.previousIcon}
              />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={STRINGS.guardian.kids.nextMonth}
              hitSlop={8}
              onPress={handleNextMonth}
              style={({ pressed }) => [styles.navButton, pressed ? styles.navPressed : null]}>
              <SvgIcon name="IcChevronRight" size={scale(24)} color={guardianColors['6']} />
            </Pressable>
          </Stack>
        </Stack>

        {isWeekdayInside ? weekdayRow : null}

        <Stack flex={1} style={styles.weeks}>
          {weeks.map(week => (
            <Stack key={week[0].toISOString()} direction="row" style={styles.weekRow}>
              {week.map(day => {
                const dateKey = format(day, 'yyyy-MM-dd');
                return (
                  <FamilyCalendarDayCell
                    key={dateKey}
                    day={day}
                    month={month}
                    selectedDate={selectedDate}
                    today={today}
                    events={isSameMonth(day, month) ? (eventsByDate.get(dateKey) ?? []) : []}
                    onSelectDay={onSelectDay}
                  />
                );
              })}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  monthLabel: {
    color: guardianColors['10'],
  },
  navButton: {
    width: scale(32),
    height: scale(32),
    borderRadius: scale(16),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['1'],
  },
  navPressed: {
    backgroundColor: guardianColors['3'],
  },
  previousIcon: {
    transform: [{ rotate: '180deg' }],
  },
  weekday: {
    color: guardianColors['6'],
    textAlign: 'center',
  },
  weekdayRowInside: {
    paddingVertical: scale(6),
  },
  weekdayRowOutside: {
    paddingBottom: scale(8),
  },
  weeks: {
    minHeight: 0,
  },
  weekRow: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    minHeight: scale({ phone: 0, tablet: 72 }),
    borderTopWidth: 1,
    borderTopColor: guardianColors['2'],
    overflow: 'hidden',
  },
}));

export default memo(FamilyCalendar);
